import { ChevronDown } from "lucide-react";

const SMRAlgorithm = () => {
  return (
    <div className="bg-card border border-border rounded-lg p-6 space-y-4" data-testid="smr-algorithm">
      <h3 className="font-heading text-lg font-bold text-center mb-6">SMR Rule Out Algorithm</h3>
      
      {/* Start */}
      <div className="flex flex-col items-center">
        <div className="bg-primary/20 border-2 border-primary rounded-lg px-4 py-2 text-center font-medium">
          Maintain manual stabilization until SMR can be ruled out
        </div>
        <div className="w-0.5 h-6 bg-border"></div>
        <ChevronDown className="w-4 h-4 text-muted-foreground -mt-1" />
      </div>
      
      {/* Question 1 */}
      <div className="flex flex-col items-center">
        <div className="bg-blue-500/20 border-2 border-blue-500 rounded-lg px-4 py-3 text-center max-w-md">
          <p className="font-medium">Alert, cooperative and stable with no neurological symptoms?</p>
        </div>
        <div className="flex items-center justify-center gap-16 mt-2">
          <div className="flex flex-col items-center">
            <span className="text-xs text-red-400 font-medium">NO</span>
            <div className="w-0.5 h-4 bg-red-400"></div>
          </div>
          <div className="flex flex-col items-center">
            <span className="text-xs text-green-400 font-medium">YES</span>
            <div className="w-0.5 h-4 bg-green-400"></div>
          </div>
        </div>
      </div>
      
      {/* Branch paths */}
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col items-center">
          <div className="bg-red-500/20 border border-red-500/50 rounded-lg px-3 py-2 text-center text-sm">
            <p className="font-medium text-red-400">Initiate Appropriate SMR</p>
          </div>
        </div>
        <div className="flex flex-col items-center">
          <div className="bg-blue-500/20 border border-blue-500/50 rounded-lg px-3 py-2 text-center text-sm">
            <p>Neck pain following blunt force trauma?</p>
          </div>
          <div className="w-0.5 h-4 bg-border"></div>
          <div className="flex items-center justify-center gap-8">
            <div className="flex flex-col items-center">
              <span className="text-xs text-muted-foreground">NO</span>
              <div className="w-0.5 h-3 bg-border"></div>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-xs text-muted-foreground">YES</span>
              <div className="w-0.5 h-3 bg-border"></div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Second level decisions */}
      <div className="grid grid-cols-2 gap-4">
        <div></div>
        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col items-center">
            <div className="bg-yellow-500/20 border border-yellow-500/50 rounded px-2 py-1 text-xs text-center">
              *High Risk Factors?
            </div>
            <div className="w-0.5 h-3 bg-border"></div>
            <div className="flex gap-4 text-xs">
              <div className="flex flex-col items-center">
                <span className="text-red-400">YES</span>
                <div className="w-0.5 h-2 bg-red-400"></div>
                <div className="bg-red-500/20 rounded px-1 py-0.5 text-red-400 text-center">SMR</div>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-green-400">NO</span>
                <div className="w-0.5 h-2 bg-green-400"></div>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center">
            <div className="bg-yellow-500/20 border border-yellow-500/50 rounded px-2 py-1 text-xs text-center">
              **Low Risk Factors?
            </div>
            <div className="w-0.5 h-3 bg-border"></div>
            <div className="flex gap-4 text-xs">
              <div className="flex flex-col items-center">
                <span className="text-red-400">NO</span>
                <div className="w-0.5 h-2 bg-red-400"></div>
                <div className="bg-red-500/20 rounded px-1 py-0.5 text-red-400 text-center">SMR</div>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-green-400">YES</span>
                <div className="w-0.5 h-2 bg-green-400"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Range of motion assessment */}
      <div className="flex justify-end pr-4">
        <div className="flex flex-col items-center max-w-[200px]">
          <div className="bg-blue-500/20 border border-blue-500/50 rounded-lg px-3 py-2 text-center text-sm">
            <p>Able to actively rotate neck 45° left and right?</p>
          </div>
          <div className="flex items-center justify-center gap-8 mt-2">
            <div className="flex flex-col items-center">
              <span className="text-xs text-red-400">NO</span>
              <div className="w-0.5 h-3 bg-red-400"></div>
              <div className="bg-red-500/20 border border-red-500/50 rounded px-2 py-1 text-xs text-red-400">SMR</div>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-xs text-green-400">YES</span>
              <div className="w-0.5 h-3 bg-green-400"></div>
              <div className="bg-green-500/20 border border-green-500/50 rounded px-2 py-1 text-xs text-green-400">SMR Not Required</div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Risk Factors Legend */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 pt-4 border-t border-border">
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
          <h4 className="font-semibold text-yellow-500 text-sm mb-2">*High-Risk Factors (Indicates SMR)</h4>
          <ul className="text-xs space-y-1 text-muted-foreground">
            <li>• Age ≥ 65 years</li>
            <li>• Paresthesia in extremities</li>
            <li>• Dangerous Mechanism:</li>
            <li className="ml-3">- Fall from elevation ≥ 3 feet / 5 stairs</li>
            <li className="ml-3">- Axial load to head (e.g. diving)</li>
            <li className="ml-3">- MVC high speed (&gt;100km/hr), rollover, ejection</li>
            <li className="ml-3">- Motorized recreational vehicles</li>
            <li className="ml-3">- Bicycle struck or collision</li>
          </ul>
        </div>
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
          <h4 className="font-semibold text-green-500 text-sm mb-2">**Low-Risk Factors (Safe ROM Assessment)</h4>
          <ul className="text-xs space-y-1 text-muted-foreground">
            <li>• Simple rear-end MVC</li>
            <li>• Sitting unsupported post incident</li>
            <li>• Ambulatory post incident</li>
            <li>• Delayed onset of neck pain</li>
            <li>• Absence of midline c-spine tenderness</li>
          </ul>
        </div>
      </div>
      
      {/* Confounding Factors */}
      <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
        <h4 className="font-semibold text-orange-500 text-sm mb-2">Confounding Factors (May Invalidate Exam)</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 text-xs text-muted-foreground">
          <span>• Acute Stress Reaction</span>
          <span>• Altered LOC</span>
          <span>• Intoxication</span>
          <span>• Age</span>
          <span>• Distracting Injuries</span>
          <span>• Communication Difficulties</span>
          <span>• Fear / Pain</span>
          <span>• Vertebral Disease</span>
        </div>
      </div>
    </div>
  );
};

export default SMRAlgorithm;
